// The `$name` template grammar (grammar 2) of journey, tool and surface fields: one parser and renderer for every
// template field. Stored form is canonical: `$$` is a literal `$`, `$name` / `${name}` a reference, any other `$` a
// syntax error. Unmarked (grammar 1) stored text is translated on read: variable-grammar-v1.heuristics.ts.
//
// Not templates (never parsed): store targets `variable` (variable-name.ts), `attribute`, journey/global/env `init`,
// `paramsSource: 'observation'` params, a component target's `selector` with `surfaceId` + `path`, `label`,
// `controlName`, `surfaceId`, `repairObjective`, `captured_session.*`, `reviewer_expectation`, `variables[].format`.
// Which strings of a step are template fields: template-fields.ts.
//
// Import-free leaf: shared by the journey and exploration runners, app tools, surfaces and server code.

// Text segments hold decoded text.
export type Segment = string | { readonly ref: string }

// The sink policy of each field: what `render` does with an unbound reference.
export const TEMPLATE_FIELDS = {
  prose: 'display',
  selector: 'strict',
  value: 'strict',
  fill: 'strict',
  target: 'target',
  argument: 'strict',
  toolInit: 'literal',
} as const satisfies Record<string, 'strict' | 'target' | 'display' | 'literal'>

export type TemplateField = keyof typeof TEMPLATE_FIELDS

export interface Template {
  readonly field: TemplateField
  readonly segments: readonly Segment[]
}

export const TEMPLATE_SYNTAX_RULE = '`$name` inserts run variable `name` (`${name}` when a letter, digit or `_` follows). Every other `$` is written `$$`: `$$5`, `{"$$class":…}`, `$$$$` for a literal `$$`. Copy pinned values verbatim, including `$$`.'

/** A template names a variable the run holds no usable value for. */
export class UnresolvedVariableError extends Error {
  constructor(token: string, variables: ReadonlyMap<string, string>) {
    const available = [...variables.keys()].map(key => `$${key}`).join(', ') || '(none)'
    super(`Unresolved variable ${token}: the run holds no value for it. To enter a run value, bind ${token} before this step (available variables: ${available}). To enter the literal text ${token}, write it as $${token} in this value ($$ enters one $).`)
    this.name = 'UnresolvedVariableError'
  }
}

/** Stored or authored text that is not grammar 2. */
export class TemplateSyntaxError extends Error {
  constructor(readonly text: string, readonly index: number) {
    super(`Invalid template ${JSON.stringify(text)} at index ${index}: write a literal $ as $$, a reference as $name or \${name}`)
    this.name = 'TemplateSyntaxError'
  }
}

export function isVariableName(name: string): boolean {
  return /^[A-Za-z_]\w*$/.test(name)
}

const IDENT = /^[A-Za-z_][A-Za-z0-9_]*/
const BRACED = /^\{([A-Za-z_][A-Za-z0-9_]*)\}/
const IDENT_CHAR = /^[A-Za-z0-9_]/

function pushText(segments: Segment[], text: string): void {
  if (!text) return
  const last = segments.length - 1
  const previous = segments[last]
  if (typeof previous === 'string') segments[last] = previous + text
  else segments.push(text)
}

// The token at a `$` with its source length: an escaped `$`, a reference, or null when it is not grammar 2.
function tokenAt(text: string, index: number): { text: string; length: number } | { ref: string; length: number } | null {
  const rest = text.slice(index + 1)
  if (rest.startsWith('$')) return { text: '$', length: 2 }
  const braced = BRACED.exec(rest)
  if (braced) return { ref: braced[1]!, length: braced[0].length + 1 }
  const bare = IDENT.exec(rest)
  return bare ? { ref: bare[0], length: bare[0].length + 1 } : null
}

export function parse(text: string, field: TemplateField): Template {
  const segments: Segment[] = []
  let end = 0
  for (let index = text.indexOf('$'); index >= 0; index = text.indexOf('$', end)) {
    const token = tokenAt(text, index)
    if (!token) throw new TemplateSyntaxError(text, index)
    pushText(segments, text.slice(end, index))
    if ('ref' in token) segments.push({ ref: token.ref })
    else pushText(segments, token.text)
    end = index + token.length
  }
  pushText(segments, text.slice(end))
  return { field, segments }
}

/** The canonical stored text: every literal `$` doubled, `${name}` only where an identifier character follows. */
export function serialize(template: Template): string {
  const segments: Segment[] = []
  for (const segment of template.segments) {
    if (typeof segment === 'string') pushText(segments, segment)
    else segments.push(segment)
  }
  return segments.map((segment, i) => {
    if (typeof segment === 'string') return segment.split('$').join('$$')
    const next = segments[i + 1]
    return typeof next === 'string' && IDENT_CHAR.test(next) ? `\${${segment.ref}}` : `$${segment.ref}`
  }).join('')
}

function boundValue(field: TemplateField, name: string, variables: ReadonlyMap<string, string>): string {
  const value = TEMPLATE_FIELDS[field] === 'literal' ? undefined : variables.get(name)
  if (value === undefined) throw new UnresolvedVariableError(`$${name}`, variables)
  return value
}

/** Substitute every reference once; rendered values are never rescanned. An unbound reference fails by the field's sink policy. */
export function render(template: Template, variables: ReadonlyMap<string, string>): string {
  const policy = TEMPLATE_FIELDS[template.field]
  if (policy === 'display') return display(template, variables)
  if (policy === 'target') {
    const missing = refs(template).find(name => variables.get(name) === undefined)
    if (missing !== undefined) throw new Error(`Missing target parameter variable: ${missing}`)
  }
  return template.segments.map(segment => typeof segment === 'string' ? segment : boundValue(template.field, segment.ref, variables)).join('')
}

/** The decoded text of a template that must hold no reference (a bound tool `init`): a reference throws. */
export function literalText(template: Template): string {
  return render({ field: 'toolInit', segments: template.segments }, new Map())
}

/** The text a reader sees: bound references show their value, unbound ones `$name`. Never throws; not a sink render. */
export function display(template: Template, variables: ReadonlyMap<string, string> = new Map()): string {
  return template.segments.map(segment => typeof segment === 'string' ? segment : variables.get(segment.ref) ?? `$${segment.ref}`).join('')
}

/** Referenced names in occurrence order, repeats kept. */
export function refs(template: Template): string[] {
  return template.segments.flatMap(segment => typeof segment === 'string' ? [] : [segment.ref])
}

/** Whether any segment is literal text. */
export function hasLiteralText(template: Template): boolean {
  return template.segments.some(segment => typeof segment === 'string')
}

/** The name when the whole template is exactly one reference. */
export function soleRef(template: Template): string | undefined {
  const [only, ...rest] = template.segments
  return only !== undefined && typeof only !== 'string' && !rest.length ? only.ref : undefined
}

/** Splice each bound reference's segments in place; unbound references stay. */
export function bind(template: Template, bindings: ReadonlyMap<string, Template>): Template {
  const segments: Segment[] = []
  for (const segment of template.segments) {
    const bound = typeof segment === 'string' ? undefined : bindings.get(segment.ref)
    for (const part of bound ? bound.segments : [segment]) {
      if (typeof part === 'string') pushText(segments, part)
      else segments.push(part)
    }
  }
  return { field: template.field, segments }
}

/** The stored text of a reference to `name`. */
export function reference(name: string, _field: TemplateField): string {
  return `$${name}`
}

/** The stored text of the literal `text`. */
export function literal(text: string, _field: TemplateField): string {
  return text.split('$').join('$$')
}

/** Canonical stored text of authored text: each unambiguous lone `$` (no `$`, identifier or `{` follows) escaped. A malformed `${` throws. */
export function normalizeAuthored(text: string): string {
  return serialize(parse(text.replace(/\$\$|\$(?![$A-Za-z_{])/g, match => match === '$$' ? match : '$$'), 'prose'))
}

/** `normalizeAuthored` for text no one can be asked to fix: a malformed `${` is read as its one reading, a literal. */
export function normalizeUnattended(text: string): string {
  return serialize(parse(escapeUntokenized(text), 'prose'))
}

// Every `$` that starts no token doubled; tokens (`$$`, `$name`, `${name}`) kept.
function escapeUntokenized(text: string): string {
  let out = ''
  let end = 0
  for (let index = text.indexOf('$'); index >= 0; index = text.indexOf('$', end)) {
    const token = tokenAt(text, index)
    out += text.slice(end, index) + (token ? text.slice(index, index + token.length) : '$$')
    end = index + (token?.length ?? 1)
  }
  return out + text.slice(end)
}
